import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, PlayCircle, Server, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { SectionTitle } from "@/components/shared";
import { moduleMeta } from "@/lib/icons";
import { adminApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function IngestionTab({ modules }) {
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState("global");
  const [maxRows, setMaxRows] = useState(400);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);
  const nameOf = (k) => (modules.find((m) => m.key === k)?.name || k);

  const loadIngestions = async () => {
    try {
      const r = await adminApi.get("/admin/ingestions");
      const list = r.data.ingestions || [];
      setHistory(list);
      const running = list.find((i) => i.status === "running" || i.status === "queued");
      setActive(running || list[0] || null);
    } catch (e) {}
  };

  useEffect(() => {
    loadIngestions();
    pollRef.current = setInterval(loadIngestions, 2500);
    return () => clearInterval(pollRef.current);
  }, []);

  const startUpload = async () => {
    if (!file) {
      toast.error("Choose an .xlsx file first.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("job_id", jobId || "global");
    fd.append("max_rows", String(maxRows || 400));
    try {
      await adminApi.post("/admin/ingest/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Secure ingestion started. Raw file stored server-side only.");
      setFile(null);
      loadIngestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const startDefault = async () => {
    const fd = new FormData();
    fd.append("job_id", jobId || "global");
    fd.append("max_rows", String(maxRows || 400));
    try {
      await adminApi.post("/admin/ingest/default", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ingesting staged workbook...");
      loadIngestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No staged workbook found");
    }
  };

  const st = active?.status;
  const perModule = active?.per_module || {};

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Upload */}
      <Card className="p-6 bg-card border-border">
        <SectionTitle>Secure Upload</SectionTitle>
        <label
          htmlFor="ingestion-upload-input"
          className="mt-4 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/50 transition-colors py-10 cursor-pointer"
        >
          <UploadCloud className="h-8 w-8 text-primary mb-2" />
          <div className="text-sm font-medium">{file ? file.name : "Drop or select an Excel memory file"}</div>
          <div className="text-xs text-muted-foreground mt-1">.xlsx / .xlsm — stored server-side, never served</div>
          <input
            id="ingestion-upload-input"
            data-testid="ingestion-upload-input"
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-xs text-muted-foreground">Job / Order scope</Label>
            <Input value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="ingestion-jobid-input" className="mt-1 bg-secondary/40" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Max rows / sheet</Label>
            <Input type="number" value={maxRows} onChange={(e) => setMaxRows(Number(e.target.value))} data-testid="ingestion-maxrows-input" className="mt-1 bg-secondary/40" />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button onClick={startUpload} className="flex-1" data-testid="ingestion-start-button">
            <PlayCircle className="h-4 w-4" /> Start Ingestion
          </Button>
          <Button onClick={startDefault} variant="secondary" data-testid="ingestion-default-button">
            <Server className="h-4 w-4" /> Ingest Staged File
          </Button>
        </div>
      </Card>

      {/* Progress */}
      <Card className="p-6 bg-card border-border" data-testid="ingestion-progress-card">
        <div className="flex items-center justify-between">
          <SectionTitle>Ingestion Status</SectionTitle>
          {st === "running" && <span className="flex items-center gap-1.5 text-xs text-primary"><Loader2 className="h-3.5 w-3.5 animate-spin" /> running</span>}
          {st === "completed" && <span className="flex items-center gap-1.5 text-xs text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /> completed</span>}
          {st === "failed" && <span className="flex items-center gap-1.5 text-xs text-rose-300"><XCircle className="h-3.5 w-3.5" /> failed</span>}
        </div>

        {!active ? (
          <div className="mt-6 text-sm text-muted-foreground">No ingestion yet. Upload a file or ingest the staged workbook to build the memory index.</div>
        ) : (
          <>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-mono text-muted-foreground truncate">{active.source} • {active.current_sheet || "—"}</span>
                <span className="tabular-nums">{active.progress || 0}%</span>
              </div>
              <Progress value={active.progress || 0} data-testid="ingestion-progress-bar" className="h-2" />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <MiniStat label="Sheets" value={`${active.processed_sheets || 0}/${active.total_sheets || 0}`} />
              <MiniStat label="Rows Indexed" value={(active.rows_indexed || 0).toLocaleString()} />
              <MiniStat label="Redactions" value={(active.redactions || 0).toLocaleString()} accent="text-rose-300" />
            </div>
            {active.error && <div className="mt-3 text-xs text-rose-300">{active.error}</div>}

            <div className="mt-5">
              <SectionTitle>Module Coverage</SectionTitle>
              <div className="mt-3 max-h-52 overflow-y-auto scrollbar-thin" data-testid="ingestion-module-coverage-table">
                {Object.entries(perModule).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
                  const meta = moduleMeta(k);
                  const Icon = meta.Icon;
                  return (
                    <div key={k} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                      <span className={cn("inline-flex items-center gap-1.5 text-sm", meta.color)}>
                        <Icon className="h-3.5 w-3.5" /> {nameOf(k)}
                      </span>
                      <span className="text-sm tabular-nums text-muted-foreground">{v.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </Card>

      {/* History */}
      <Card className="p-6 bg-card border-border lg:col-span-2">
        <SectionTitle>Ingestion History</SectionTitle>
        <div className="mt-3 divide-y divide-border/50">
          {history.length === 0 && <div className="text-sm text-muted-foreground py-4">No ingestion runs yet.</div>}
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between py-2.5 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium truncate">{h.source}</span>
                <span className="text-xs text-muted-foreground font-mono">job:{h.job_id}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="tabular-nums">{(h.rows_indexed || 0).toLocaleString()} rows</span>
                <span className="tabular-nums text-rose-300">{(h.redactions || 0).toLocaleString()} redacted</span>
                <StatusBadge status={h.status} />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function MiniStat({ label, value, accent = "text-foreground" }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn("mt-1 text-lg font-display font-semibold tabular-nums", accent)}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    completed: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
    running: "text-primary border-primary/30 bg-primary/10",
    queued: "text-blue-300 border-blue-500/30 bg-blue-500/10",
    failed: "text-rose-300 border-rose-500/30 bg-rose-500/10",
  };
  return <span className={cn("rounded-full border px-2 py-0.5 text-[11px]", map[status] || "border-border")}>{status}</span>;
}
